/**
 * Permissions: what a role or one person may do alone, and the most they may
 * do before asking.
 *
 * Two records. A role is how most people get a permission. An override on one
 * person beats every role — and with an expiry it is how somebody covers for
 * a supervisor who is away, and stops covering by itself when they are back.
 *
 * That is not acting on behalf of the supervisor. A deputy with an override
 * acts as themselves, and their own UUID goes on every audit entry they
 * cause. Same outcome, different accountability, so the two are never the
 * same control.
 */

import type { PermissionListItem, PermissionRecord } from "@wallyo/contracts/console/configuration";
import { useMemo, useState } from "react";

import { listPermissions, listRoles, savePermissions } from "../../api/console-api";
import { useApi } from "../../api/context";
import { categoryName, featureName, permissionName, t } from "../../i18n";
import { PageTop } from "../../shell/PageTop";
import { LoadFailed, Loading, useLoad } from "../config-parts";
import { describeConfigFailure } from "../config-refusal";
import { cleanUuid, isCompleteUuid } from "../features/feature-save-model";
import { countLabel, SaveBar } from "../features/FeatureTableView";
import { WriteFailureNotice, useIntentWrite } from "../team/TeamDialogs";
import { useHash } from "../../navigation/use-hash";
import { useAlerts } from "../../shell/alerts";

type Tab = "role" | "employee";

const describeFailure = (error: unknown) =>
  describeConfigFailure(error, { critical: "features.refused.critical" }, "features.stale");

/** The grants that moved, and the expiry chosen with them. */
type PermissionDraft = Readonly<Record<string, { held: boolean | null; expiresAt: string | null }>>;

/**
 * The record a link asked for: `#permissions/role/<staffRoleId>` or
 * `#permissions/employee/<uuid>`. Anything else is null, and the screen opens
 * on its own empty state.
 */
export function openedBy(hash: string): PermissionRecord | null {
  const rest = hash.replace(/^#\/?/, "");
  if (!rest.startsWith("permissions/")) return null;
  const [kind, value] = rest.slice("permissions/".length).split("/");
  const id = decodeURIComponent(value ?? "").trim();
  if (!id) return null;
  if (kind === "role") return { kind: "role", staffRoleId: id };
  if (kind === "employee" && /^\d+$/.test(id)) return { kind: "employee", uuid: Number(id) };
  return null;
}

/** Where a link should point to open Permissions on one role. */
export function permissionsHref(staffRoleId: string): string {
  return `#permissions/role/${encodeURIComponent(staffRoleId)}`;
}

export function PermissionsScreen() {
  const { client } = useApi();
  // `#permissions/role/<staffRoleId>` opens on that role, so a link from
  // somewhere else lands on the thing it named instead of on the list with
  // the picker still to be used. A hash that names nothing opens as before.
  const opening = openedBy(useHash());
  const [tab, setTab] = useState<Tab>(opening?.kind ?? "role");
  const [roleId, setRoleId] = useState(opening?.kind === "role" ? opening.staffRoleId : "");
  const [uuid, setUuid] = useState("");
  const [record, setRecord] = useState<PermissionRecord | null>(opening ?? null);
  const [searches, setSearches] = useState(0);
  const [problem, setProblem] = useState<string | null>(null);
  const [draft, setDraft] = useState<PermissionDraft>({});
  const [reason, setReason] = useState("");
  const alerts = useAlerts();
  const save = useIntentWrite();

  const key = record === null ? null : `${record.kind === "role" ? record.staffRoleId : record.uuid}#${searches}`;
  const list = useLoad(record === null ? null : () => listPermissions(client, record), key);
  // The roles the picker offers are read on their own, not out of a record's
  // answer: you cannot pick the role whose permissions to read until you have
  // been shown the roles, so reading them from that answer leaves the picker
  // permanently empty.
  const roleList = useLoad(() => listRoles(client), "roles");

  const loading = record !== null && list.state.status === "loading";
  const permissions = list.state.data?.permissions ?? [];
  const roles = roleList.state.data?.roles ?? [];
  const changed = useMemo(
    () => permissions.filter((each) => draft[each.settingId] !== undefined),
    [permissions, draft],
  );

  // A role's permissions and a person's are different tables: a draft must
  // never cross between them.
  const reset = () => {
    setDraft({});
    setReason("");
    setProblem(null);
  };

  const open = (next: PermissionRecord) => {
    reset();
    setRecord(next);
    setSearches((count) => count + 1);
  };

  const search = () => {
    if (tab === "role") {
      if (roleId === "") return setProblem(t("permissions.pickRole"));
      return open({ kind: "role", staffRoleId: roleId });
    }
    if (!isCompleteUuid(uuid)) return setProblem(t("features.subject.needsUuid"));
    return open({ kind: "employee", uuid: Number(cleanUuid(uuid)) });
  };

  const submit = () => {
    if (record === null) return;
    save.edited();
    const changes = changed.map((permission) => {
      const moved = draft[permission.settingId];
      return {
        settingId: permission.settingId,
        held: moved.held,
        expiresAt: moved.expiresAt,
        ...(permission.valueVersion === undefined ? {} : { version: permission.valueVersion }),
      };
    });
    void save.run(
      changes.length === 0
        ? t("features.save.nothing")
        : reason.trim().length < 3
          ? t("features.save.reasonNeeded")
          : null,
      (idempotencyKey) => savePermissions(client, { record, reason: reason.trim(), changes }, { idempotencyKey }),
      ({ saved }) => {
        reset();
        alerts.ok(countLabel("done", saved));
        list.reload();
      },
    );
  };

  const shownHeld = (permission: PermissionListItem) => draft[permission.settingId]?.held ?? permission.held;

  return (
    <>
      <PageTop title={t("permissions.title")} />
      <div className="surface-tabs" role="tablist">
        {(["role", "employee"] as const).map((each) => (
          <button
            key={each}
            type="button"
            role="tab"
            aria-selected={tab === each}
            className={tab === each ? "surface-tab on" : "surface-tab"}
            onClick={() => {
              if (each === tab) return;
              setTab(each);
              setRecord(null);
              reset();
            }}
          >
            {t(`permissions.tab.${each}` as const)}
          </button>
        ))}
      </div>

      <form
        className="card subject-bar"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          search();
        }}
      >
        {tab === "role" ? (
          <label className="subject-uuid">
            <span className="subject-label">{t("permissions.tab.role")}</span>
            <select value={roleId} onChange={(event) => setRoleId(event.target.value)}>
              <option value="">{t("permissions.pickRole")}</option>
              {roles.map((role) => (
                <option key={role.staffRoleId} value={role.staffRoleId}>
                  {role.name}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <label className="subject-uuid">
            <span className="subject-label">{t("features.subject.employeeUuid")}</span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={uuid}
              placeholder={t("features.subject.staffUuidHint")}
              onChange={(event) => {
                setProblem(null);
                setUuid(cleanUuid(event.target.value));
              }}
            />
          </label>
        )}
        <button type="submit" className="btn primary" disabled={loading}>
          {loading ? t("features.subject.searching") : t("features.subject.search")}
        </button>
        {problem ? <span className="subject-problem">{problem}</span> : null}
        <span className="subject-who">
          {list.state.data ? (
            <span className="subject-pill">
              <span className="subject-name">{list.state.data.subject.label}</span>
              {list.state.data.subject.roles?.length ? (
                <>
                  <span className="subject-sep" aria-hidden="true" />
                  <span className="subject-kind-tag">{list.state.data.subject.roles.join(", ")}</span>
                </>
              ) : null}
            </span>
          ) : null}
        </span>
      </form>

      {record === null ? (
        <div className="card">
          <div className="empty">{t("features.subject.pickFirst")}</div>
        </div>
      ) : null}
      {record !== null && list.state.status === "failed" ? (
        <LoadFailed
          message={t("features.loadFailed")}
          retry={t("features.retry")}
          error={list.state.error}
          onRetry={list.reload}
        />
      ) : null}
      {!list.state.data && loading ? <Loading text={t("features.loading")} /> : null}

      {list.state.data ? (
        <>
          <div className="card">
            <table className="config-list feature-table">
              <thead>
                <tr>
                  <th>{t("permissions.title")}</th>
                  <th>{t("features.col.category")}</th>
                  <th>{t("features.col.source")}</th>
                  <th>{t("permissions.col.limit")}</th>
                  <th>{t("features.col.value")}</th>
                </tr>
              </thead>
              <tbody>
                {permissions.map((permission) => {
                  const moved = draft[permission.settingId] !== undefined;
                  const on = shownHeld(permission);
                  return (
                    <tr key={permission.settingId} className={moved ? "dirty" : undefined}>
                      <td>
                        {permissionName(permission.settingId)}
                        {/* A permission on a feature that is off cannot apply at all. */}
                        {permission.approvable ? (
                          <span className="pill quiet">{t("permissions.approvable")}</span>
                        ) : null}
                      </td>
                      <td>{categoryName(permission.category)}</td>
                      <td className="source-cell">
                        <span className={permission.source === "staffMember" ? "pill own" : "muted"}>
                          {permission.source === "default"
                            ? t("permissions.source.default")
                            : permission.source === "staffRole" && permission.grantedBy.length > 0
                              ? `${t("permissions.source.staffRole")}: ${permission.grantedBy.join(", ")}`
                              : t(`permissions.source.${permission.source}` as const)}
                        </span>
                      </td>
                      <td>
                        {!permission.hasLimit ? (
                          <span className="muted">—</span>
                        ) : permission.limits.length === 0 ? (
                          <span className="muted">{t("permissions.noLimit")}</span>
                        ) : (
                          permission.limits.map((limit) => (
                            <span key={limit.currency} className="pill quiet">
                              {limit.amount.toLocaleString()} {limit.currency}
                            </span>
                          ))
                        )}
                      </td>
                      <td>
                        <span className="value-cell">
                          <button
                            type="button"
                            role="switch"
                            aria-checked={on}
                            aria-label={permissionName(permission.settingId)}
                            className={`toggle${on ? " on" : ""}${moved ? " dirty" : ""}`}
                            disabled={!permission.editable}
                            onClick={() => {
                              save.edited();
                              setDraft((current) => {
                                const { [permission.settingId]: dropped, ...rest } = current;
                                if (dropped !== undefined) return rest;
                                return { ...rest, [permission.settingId]: { held: !on, expiresAt: null } };
                              });
                            }}
                          />
                          <span className="value-word">{t(on ? "features.value.on" : "features.value.off")}</span>
                          {/* An expiry is what makes covering for somebody safe:
                              it revokes itself and nobody has to remember. */}
                          {moved && draft[permission.settingId].held === true ? (
                            <label className="value-expiry">
                              <span>{t("permissions.col.expires")}</span>
                              <input
                                type="date"
                                value={draft[permission.settingId].expiresAt?.slice(0, 10) ?? ""}
                                onChange={(event) =>
                                  setDraft((current) => ({
                                    ...current,
                                    [permission.settingId]: {
                                      held: true,
                                      expiresAt:
                                        event.target.value === ""
                                          ? null
                                          : new Date(`${event.target.value}T23:59:59Z`).toISOString(),
                                    },
                                  }))
                                }
                              />
                            </label>
                          ) : null}
                          {moved ? <span className="value-note unsaved">{t("features.unsaved")}</span> : null}
                          {!permission.editable ? (
                            <span className="value-note locked">{featureName(permission.feature as never)}</span>
                          ) : null}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {save.error ? <WriteFailureNotice failure={describeFailure(save.error)} onReload={list.reload} /> : null}
          <SaveBar
            count={changed.length}
            reason={reason}
            pending={save.pending}
            problem={save.problem}
            onReason={(next) => {
              save.edited();
              setReason(next);
            }}
            onDiscard={reset}
            onSave={submit}
          />
        </>
      ) : null}
    </>
  );
}
